import React, { createContext, useContext, useReducer, useEffect, ReactNode, useRef, useCallback } from 'react'
import { TimeEntry, Project } from '../types'
import { timerService, timeUtils } from '../services/timerService'
import { projectService } from '../services/projectService'
import { timesheetService, WeekLockStatus } from '../services/timesheetService'
import { useAuth } from './AuthContext'

// 計時器狀態介面
interface TimerState {
  // 計時器狀態
  activeTimer: TimeEntry | null
  isRunning: boolean
  elapsedTime: number
  
  // 專案相關
  projects: Project[]
  selectedProject: Project | null
  recentFavoriteProjects: Project[]
  
  // 工作描述
  description: string
  isDescriptionModified: boolean // 追蹤用戶是否已修改描述
  
  // 今日記錄
  todayEntries: TimeEntry[]
  totalTodayDuration: number
  
  // 週次鎖定狀態
  weekLockStatus: WeekLockStatus | null
  isCheckingWeekLock: boolean
  
  // 載入狀態
  isLoading: boolean
  error: string | null
}

// 動作類型
type TimerAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PROJECTS'; payload: Project[] }
  | { type: 'SET_SELECTED_PROJECT'; payload: Project | null }
  | { type: 'SET_RECENT_FAVORITE_PROJECTS'; payload: Project[] }
  | { type: 'SET_DESCRIPTION'; payload: string }
  | { type: 'SET_DESCRIPTION_MODIFIED'; payload: boolean }
  | { type: 'SET_ACTIVE_TIMER'; payload: TimeEntry | null }
  | { type: 'SET_IS_RUNNING'; payload: boolean }
  | { type: 'SET_ELAPSED_TIME'; payload: number }
  | { type: 'INCREMENT_ELAPSED_TIME' }
  | { type: 'SET_TODAY_ENTRIES'; payload: { entries: TimeEntry[]; totalDuration: number } }
  | { type: 'ADD_TODAY_ENTRY'; payload: TimeEntry }
  | { type: 'UPDATE_TODAY_ENTRY'; payload: TimeEntry }
  | { type: 'REMOVE_TODAY_ENTRY'; payload: string }
  | { type: 'SET_WEEK_LOCK_STATUS'; payload: WeekLockStatus | null }
  | { type: 'SET_CHECKING_WEEK_LOCK'; payload: boolean }
  | { type: 'RESET_STATE' }

// 初始狀態
const initialState: TimerState = {
  activeTimer: null,
  isRunning: false,
  elapsedTime: 0,
  projects: [],
  selectedProject: null,
  recentFavoriteProjects: [],
  description: '',
  isDescriptionModified: false,
  todayEntries: [],
  totalTodayDuration: 0,
  weekLockStatus: null,
  isCheckingWeekLock: false,
  isLoading: false,
  error: null
}

// Reducer 函數
const timerReducer = (state: TimerState, action: TimerAction): TimerState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    
    case 'SET_PROJECTS':
      return { ...state, projects: action.payload }
    
    case 'SET_SELECTED_PROJECT':
      return { ...state, selectedProject: action.payload }
    
    case 'SET_RECENT_FAVORITE_PROJECTS':
      return { ...state, recentFavoriteProjects: action.payload }
    
    case 'SET_DESCRIPTION':
      return { ...state, description: action.payload }
    
    case 'SET_DESCRIPTION_MODIFIED':
      return { ...state, isDescriptionModified: action.payload }
    
    case 'SET_ACTIVE_TIMER':
      return { 
        ...state, 
        activeTimer: action.payload,
        isRunning: action.payload?.status === 'RUNNING',
        elapsedTime: action.payload 
          ? timeUtils.calculateDuration(action.payload.startTime)
          : 0
      }
    
    case 'SET_IS_RUNNING':
      return { ...state, isRunning: action.payload }
    
    case 'SET_ELAPSED_TIME':
      return { ...state, elapsedTime: action.payload }
    
    case 'INCREMENT_ELAPSED_TIME':
      return { ...state, elapsedTime: state.elapsedTime + 1 }
    
    case 'SET_TODAY_ENTRIES':
      return { 
        ...state, 
        todayEntries: action.payload.entries,
        totalTodayDuration: action.payload.totalDuration
      }
    
    case 'ADD_TODAY_ENTRY':
      return {
        ...state,
        todayEntries: [action.payload, ...state.todayEntries],
        totalTodayDuration: state.totalTodayDuration + action.payload.duration
      }
    
    case 'UPDATE_TODAY_ENTRY':
      const updatedEntries = state.todayEntries.map(entry =>
        entry.id === action.payload.id ? action.payload : entry
      )
      const newTotalDuration = updatedEntries.reduce((sum, entry) => sum + entry.duration, 0)
      return {
        ...state,
        todayEntries: updatedEntries,
        totalTodayDuration: newTotalDuration
      }
    
    case 'REMOVE_TODAY_ENTRY':
      const filteredEntries = state.todayEntries.filter(entry => entry.id !== action.payload)
      const removedEntry = state.todayEntries.find(entry => entry.id === action.payload)
      return {
        ...state,
        todayEntries: filteredEntries,
        totalTodayDuration: state.totalTodayDuration - (removedEntry?.duration || 0)
      }
    
    case 'SET_WEEK_LOCK_STATUS':
      return { ...state, weekLockStatus: action.payload }
    
    case 'SET_CHECKING_WEEK_LOCK':
      return { ...state, isCheckingWeekLock: action.payload }
    
    case 'RESET_STATE':
      return initialState
    
    default:
      return state
  }
}

// Context 介面
interface TimerContextType extends TimerState {
  // 動作函數
  loadProjects: (forceReload?: boolean) => Promise<void>
  loadRecentFavoriteProjects: () => Promise<void>
  selectProject: (project: Project | null) => void
  setDescription: (description: string) => void
  startTimer: () => Promise<void>
  stopTimer: () => Promise<void>
  updateTimeEntry: (timeEntryId: string, data: any) => Promise<void>
  deleteTimeEntry: (timeEntryId: string) => Promise<void>
  loadTodayEntries: () => Promise<void>
  loadActiveTimer: () => Promise<void>
  createManualTimeEntry: (data: {
    projectId: string
    description?: string
    startTime: string
    endTime: string
    date: string
    manualReason: string
  }) => Promise<void>
  editTimeEntry: (data: {
    id: string
    projectId: string
    description: string
    startTime: string
    endTime: string
    originalDate?: string
  }) => Promise<TimeEntry>
  checkCurrentWeekLock: () => Promise<void>
  refreshTimerData: () => Promise<void>
}

// 建立 Context
const TimerContext = createContext<TimerContextType | undefined>(undefined)

// Provider 組件
interface TimerProviderProps {
  children: ReactNode
}

export const TimerProvider: React.FC<TimerProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(timerReducer, initialState)
  const { user, token, isLoading: authLoading } = useAuth()
  
  // 用於防抖和請求去重的 refs
  const debounceTimerRef = useRef<number | null>(null)
  const loadingRequestsRef = useRef<Set<string>>(new Set())
  const lastLoadTimeRef = useRef<{ [key: string]: number }>({})
  const lastTokenRef = useRef<string | null>(null)
  
  // 防抖函數
  const debounce = useCallback((func: () => void, delay: number) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(func, delay) as unknown as number
  }, [])
  
  // 請求去重函數
  const withRequestDeduplication = useCallback(async (
    requestKey: string,
    requestFn: () => Promise<void>,
    minInterval: number = 5000 // 5秒內不重複請求
  ) => {
    // 檢查是否正在請求中
    if (loadingRequestsRef.current.has(requestKey)) {
      return
    }
    
    // 檢查最後請求時間
    const lastLoadTime = lastLoadTimeRef.current[requestKey] || 0
    const now = Date.now()
    if (now - lastLoadTime < minInterval) {
      return
    }
    
    try {
      loadingRequestsRef.current.add(requestKey)
      lastLoadTimeRef.current[requestKey] = now
      await requestFn()
    } finally {
      loadingRequestsRef.current.delete(requestKey)
    }
  }, [])

  // 載入專案列表
  const loadProjects = useCallback(async (forceReload: boolean = false) => {
    if (forceReload) {
      // 強制重新載入時清除該請求的快取
      delete lastLoadTimeRef.current['loadProjects']
      loadingRequestsRef.current.delete('loadProjects')
    }
    
    await withRequestDeduplication('loadProjects', async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true })
        const projects = await projectService.getProjects({ isActive: true })
        dispatch({ type: 'SET_PROJECTS', payload: projects })
        dispatch({ type: 'SET_ERROR', payload: null }) // 成功後清除錯誤
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    })
  }, [withRequestDeduplication])

  // 載入最近常用專案
  const loadRecentFavoriteProjects = useCallback(async () => {
    await withRequestDeduplication('loadRecentFavoriteProjects', async () => {
      try {
        const recentProjects = await projectService.getRecentFavoriteProjects()
        dispatch({ type: 'SET_RECENT_FAVORITE_PROJECTS', payload: recentProjects })
      } catch (error) {
        // 不顯示錯誤，因為這是可選功能
        console.error('載入最近常用專案失敗:', error)
        dispatch({ type: 'SET_RECENT_FAVORITE_PROJECTS', payload: [] })
      }
    }, 10000) // 10秒內不重複請求
  }, [withRequestDeduplication])

  // 選擇專案
  const selectProject = (project: Project | null) => {
    dispatch({ type: 'SET_SELECTED_PROJECT', payload: project })
  }

  // 設定工作描述
  const setDescription = (description: string) => {
    dispatch({ type: 'SET_DESCRIPTION', payload: description })
    
    // 如果有運行中的計時器且用戶修改了描述，標記為已修改
    if (state.activeTimer && state.isRunning) {
      // 檢查描述是否真的有變更
      if (description !== state.activeTimer.description) {
        dispatch({ type: 'SET_DESCRIPTION_MODIFIED', payload: true })
      }
    }
  }

  // 開始計時
  const startTimer = async () => {
    if (!state.selectedProject) {
      dispatch({ type: 'SET_ERROR', payload: '請選擇專案' })
      return
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const timeEntry = await timerService.startTimer({
        projectId: state.selectedProject.id,
        description: state.description
      })
      
      dispatch({ type: 'SET_ACTIVE_TIMER', payload: timeEntry })
      dispatch({ type: 'SET_ERROR', payload: null })
      
      // 重新載入今日記錄
      await loadTodayEntries()
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  // 停止計時
  const stopTimer = async () => {
    if (!state.activeTimer) return

    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      // 將當前的描述狀態傳遞給 stopTimer 服務函數
      const updatedTimeEntry = await timerService.stopTimer(state.activeTimer.id, state.description)
      
      dispatch({ type: 'SET_ACTIVE_TIMER', payload: null })
      dispatch({ type: 'UPDATE_TODAY_ENTRY', payload: updatedTimeEntry })
      dispatch({ type: 'SET_ERROR', payload: null })
      
      // 清空描述並重置修改標記
      dispatch({ type: 'SET_DESCRIPTION', payload: '' })
      dispatch({ type: 'SET_DESCRIPTION_MODIFIED', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  // 更新計時記錄
  const updateTimeEntry = async (timeEntryId: string, data: any) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const updatedTimeEntry = await timerService.updateTimeEntry(timeEntryId, data)
      dispatch({ type: 'UPDATE_TODAY_ENTRY', payload: updatedTimeEntry })
      dispatch({ type: 'SET_ERROR', payload: null })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  // 刪除計時記錄
  const deleteTimeEntry = async (timeEntryId: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      await timerService.deleteTimeEntry(timeEntryId)
      dispatch({ type: 'REMOVE_TODAY_ENTRY', payload: timeEntryId })
      dispatch({ type: 'SET_ERROR', payload: null })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  // 載入今日記錄
  const loadTodayEntries = useCallback(async () => {
    await withRequestDeduplication('loadTodayEntries', async () => {
      try {
        const todayData = await timerService.getTodayEntries()
        dispatch({ 
          type: 'SET_TODAY_ENTRIES', 
          payload: { 
            entries: todayData.entries, 
            totalDuration: todayData.totalDuration 
          }
        })
        dispatch({ type: 'SET_ERROR', payload: null }) // 成功後清除錯誤
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      }
    }, 3000) // 3秒內不重複請求
  }, [withRequestDeduplication])

  // 載入進行中的計時
  const loadActiveTimer = useCallback(async () => {
    await withRequestDeduplication('loadActiveTimer', async () => {
      try {
        const activeTimer = await timerService.getActiveTimer()
        dispatch({ type: 'SET_ACTIVE_TIMER', payload: activeTimer })
        
        // 如果有進行中的計時器，自動恢復任務名稱和專案選擇
        if (activeTimer && activeTimer.status === 'RUNNING') {
          // 恢復任務名稱 - 只有在用戶沒有修改過描述的情況下才恢復
          if (activeTimer.description && !state.isDescriptionModified) {
            dispatch({ type: 'SET_DESCRIPTION', payload: activeTimer.description })
          }
          
          // 恢復專案選擇 - 需要等待專案列表載入完成
          if (activeTimer.project) {
            // 先嘗試從當前專案列表中找到對應專案
            const currentProject = state.projects.find(p => p.id === activeTimer.project.id)
            if (currentProject) {
              dispatch({ type: 'SET_SELECTED_PROJECT', payload: currentProject })
            } else {
              // 如果當前專案列表中沒有，使用計時器中的專案資料
              dispatch({ type: 'SET_SELECTED_PROJECT', payload: activeTimer.project })
            }
          }
        }
        
        dispatch({ type: 'SET_ERROR', payload: null }) // 成功後清除錯誤
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      }
    }, 3000) // 3秒內不重複請求
  }, [withRequestDeduplication, state.projects, state.isDescriptionModified])

  // 手動新增時間記錄
  const createManualTimeEntry = async (data: {
    projectId: string
    description?: string
    startTime: string
    endTime: string
    date: string
    manualReason: string
  }) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const newTimeEntry = await timerService.createManualTimeEntry(data)
      
      // 檢查是否是今天的記錄，如果是則添加到今日記錄中
      const today = new Date().toISOString().split('T')[0]
      if (data.date === today) {
        dispatch({ type: 'ADD_TODAY_ENTRY', payload: newTimeEntry })
      }
      
      dispatch({ type: 'SET_ERROR', payload: null })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error // 重新拋出錯誤，讓調用者處理
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  // 編輯時間記錄
  const editTimeEntry = async (data: {
    id: string
    projectId: string
    description: string
    startTime: string
    endTime: string
    originalDate?: string // 可選的原始日期，用於週檢視編輯
  }) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      
      let dateString: string
      
      if (data.originalDate) {
        // 如果提供了原始日期，直接使用（來自週檢視）
        dateString = data.originalDate
      } else {
        // 從今日記錄中找到原始記錄以獲取日期（來自今日檢視）
        const originalEntry = state.todayEntries.find(entry => entry.id === data.id)
        if (!originalEntry) {
          throw new Error('找不到原始記錄')
        }
        
        // 從原始記錄獲取本地日期（使用用戶本地時區）
        const originalDate = new Date(originalEntry.startTime)
        const year = originalDate.getFullYear()
        const month = String(originalDate.getMonth() + 1).padStart(2, '0')
        const day = String(originalDate.getDate()).padStart(2, '0')
        dateString = `${year}-${month}-${day}`
      }
      
      const updatedTimeEntry = await timerService.editTimeEntry({
        ...data,
        originalDate: dateString
      })
      
      // 如果是今日記錄，更新今日記錄狀態
      const today = new Date().toISOString().split('T')[0]
      if (dateString === today) {
        dispatch({ type: 'UPDATE_TODAY_ENTRY', payload: updatedTimeEntry })
      }
      
      dispatch({ type: 'SET_ERROR', payload: null })
      
      return updatedTimeEntry
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error // 重新拋出錯誤，讓調用者處理
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  // 檢查本週鎖定狀態
  const checkCurrentWeekLock = useCallback(async () => {
    await withRequestDeduplication('checkCurrentWeekLock', async () => {
      try {
        dispatch({ type: 'SET_CHECKING_WEEK_LOCK', payload: true })
        const today = new Date().toISOString().split('T')[0]
        const lockStatus = await timesheetService.checkWeekLock(today)
        dispatch({ type: 'SET_WEEK_LOCK_STATUS', payload: lockStatus })
        dispatch({ type: 'SET_ERROR', payload: null }) // 成功後清除錯誤
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      } finally {
        dispatch({ type: 'SET_CHECKING_WEEK_LOCK', payload: false })
      }
    }, 3000) // 3秒內不重複請求
  }, [withRequestDeduplication])

  // 刷新計時器相關數據
  const refreshTimerData = useCallback(async () => {
    try {
      // 清除防抖和去重緩存，強制重新載入
      lastLoadTimeRef.current = {}
      loadingRequestsRef.current.clear()
      
      // 並行載入所有相關數據
      await Promise.all([
        loadTodayEntries(),
        loadActiveTimer(),
        checkCurrentWeekLock()
      ])
    } catch (error) {
      console.error('刷新計時器數據失敗:', error)
    }
  }, [loadTodayEntries, loadActiveTimer, checkCurrentWeekLock])

  // 計時器更新效果
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null

    if (state.isRunning && state.activeTimer) {
      interval = setInterval(() => {
        dispatch({ type: 'INCREMENT_ELAPSED_TIME' })
      }, 1000)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [state.isRunning, state.activeTimer])

  // 監聽 token 變化，當 token 刷新成功後清除錯誤並重新載入數據
  useEffect(() => {
    // 檢查 token 是否有變化（表示可能發生了刷新）
    if (token && lastTokenRef.current && token !== lastTokenRef.current && user && !authLoading) {
      console.log('Token refreshed, clearing error and refreshing timer data...')
      
      // 清除現有的錯誤狀態
      dispatch({ type: 'SET_ERROR', payload: null })
      
      // 刷新計時器數據
      refreshTimerData().catch(error => {
        console.error('Token 刷新後重新載入數據失敗:', error)
      })
    }
    
    // 更新最後的 token 記錄
    lastTokenRef.current = token
  }, [token, user, authLoading, refreshTimerData])

  // 初始化載入 - 只在用戶已登入且身份驗證完成後才載入
  useEffect(() => {
    const initializeTimer = async () => {
      if (!authLoading && user) {
        try {
          // 先載入專案列表，再載入活動計時器以確保可以正確恢復專案選擇
          await loadProjects()
          await loadActiveTimer()
          
          // 其他載入可以並行進行
          await Promise.all([
            loadTodayEntries(),
            checkCurrentWeekLock(),
            loadRecentFavoriteProjects()
          ])
        } catch (error: any) {
          console.error('初始化計時器失敗:', error)
          // 如果是認證相關錯誤，不顯示錯誤給用戶
          if (!error.message?.includes('登入已過期') && !error.message?.includes('401')) {
            dispatch({ type: 'SET_ERROR', payload: '載入計時器數據失敗，請重新整理頁面' })
          }
        }
      } else if (!authLoading && !user) {
        // 用戶登出時重置狀態
        dispatch({ type: 'RESET_STATE' })
      }
    }

    initializeTimer()
  }, [user, authLoading, loadProjects, loadActiveTimer, loadTodayEntries, checkCurrentWeekLock, loadRecentFavoriteProjects])

  // 定期檢查週次鎖定狀態
  useEffect(() => {
    if (!user) return

    const checkInterval = setInterval(() => {
      checkCurrentWeekLock()
    }, 30000) // 每30秒檢查一次

    return () => {
      clearInterval(checkInterval)
    }
  }, [user, checkCurrentWeekLock])

  // 頁面可見性變化時重新載入狀態（使用防抖）
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        debounce(() => {
          loadActiveTimer()
          loadTodayEntries()
        }, 1000) // 1秒防抖
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [user, debounce, loadActiveTimer, loadTodayEntries])

  const contextValue: TimerContextType = {
    ...state,
    loadProjects,
    loadRecentFavoriteProjects,
    selectProject,
    setDescription,
    startTimer,
    stopTimer,
    updateTimeEntry,
    deleteTimeEntry,
    loadTodayEntries,
    loadActiveTimer,
    createManualTimeEntry,
    editTimeEntry,
    checkCurrentWeekLock,
    refreshTimerData
  }

  return (
    <TimerContext.Provider value={contextValue}>
      {children}
    </TimerContext.Provider>
  )
}

// Hook 來使用 Timer Context
export const useTimer = (): TimerContextType => {
  const context = useContext(TimerContext)
  if (context === undefined) {
    throw new Error('useTimer must be used within a TimerProvider')
  }
  return context
}
